// Screen-level RBAC helpers. user.screens comes from /auth/me:
// null/undefined = unrestricted (super admin or no access role assigned).

// CEO detail pages -> CEO tabs that grant them (any one is enough).
const CEO_DETAIL_TABS = {
  "shipment-performance": ["shipment"],
  "fda-release-hold": ["shipment"],
  "ship-promptness-by-plant": ["shipment"],
  "yearly-dispatch-comparison": ["shipment", "comparisons"],
  "payments-received-pending": ["commercial"],
  "pending-shipments": ["commercial", "shipment"],
  "variety-analysis": ["commercial"],
  "sales-price-analysis": ["commercial", "buyer"],
  "sale-vs-actual-price": ["commercial"],
  "stock-finance-exposure": ["commercial"],
  "price-comparison-over-years": ["comparisons"],
  "headon-cy-vs-fy": ["production", "comparisons"],
  "dummy-carton-usage-by-period": ["store"],
  "dummy-stock-pending-contracts": ["store"],
  "consumables-usage-by-plant": ["store"],
  "packing-material-contracts": ["store"],
  "chemical-stock": ["store"],
  "confirmed-orders-production-status": ["po"],
  "po-day-code-status": ["po"],
  "order-delivery-status": ["po"],
  "buyer-order-analysis": ["po", "buyer"],
  "production-plant-order-view": ["production"],
  "headon-vs-finished-plant": ["production"],
  "finished-product-volume": ["production"],
  "floor-balance": ["production"],
  "chemical-used-on-date": ["production"],
  "under-production-by-buyer": ["production"],
  "rm-purchase-value": ["purchase"],
  "purchase-quality-by-variety": ["purchase"],
  "supplier-antibiotic-positive": ["purchase", "qc"],
  "rm-arrivals-by-count": ["purchase"],
  "batch-antibiotic-history": ["qc"],
  "eu-farm-certificates": ["overview", "qc"],
  "species-analysis": ["overview"],
  "client-requirements": ["overview"],
  "master-search": ["master"],
};

const PATH_SCREENS = [
  ["/dashboards", "dashboards"],
  ["/builder", "builder"],
  ["/ms", "ms"],
  ["/ai-agent", "ai"],
  ["/reports", "reports"],
  ["/kpi", "kpi"],
  ["/exports", "exports"],
  ["/database-explorer", "database_explorer"],
  ["/erp-flow-planner", "erp_flow_planner"],
  ["/data-sources", "data_sources"],
  ["/datasets", "datasets"],
  ["/uploads", "uploads"],
  ["/sql", "sql"],
  ["/etl", "etl"],
  ["/access-management", "access_management"],
  ["/workspaces", "workspaces"],
  ["/audit", "audit"],
];

export const ceoTabScreen = (tab) => (tab === "ai" ? "ai" : `ceo.${tab || "overview"}`);

// Role-restricted users see CEO tabs as plain modules ("Sales"), not "CEO Command Center".
export const isRestricted = (user) => Array.isArray(user?.screens);

export const CEO_TAB_INFO = {
  "quick-access": ["Quick Access", "Your saved analytics in one place."],
  overview: ["Dashboard", "Executive overview across sales, purchase, QA, process, store, PO and dispatch."],
  buyer: ["Sales", "Sales and customer performance by buyer, consignee, destination, grade and brand."],
  purchase: ["Purchase", "Raw material purchase and procurement analytics."],
  qc: ["QA", "Quality, antibiotic testing and farm certification analytics."],
  production: ["Process", "Preprocessing, value addition, freezing and packing analytics."],
  store: ["Store", "Cold store stock, transfers and PO-linked stock."],
  po: ["PO", "Purchase order fulfilment, coverage and risk."],
  shipment: ["Dispatch", "Shipment, dispatch and delivery performance."],
  commercial: ["Commercial", "Sales value, collections, payments and pricing."],
  comparisons: ["Comparisons", "Year-on-year and period comparisons."],
  master: ["Master Search", "Search ERP records across modules."],
  ai: ["AI Ask", "Ask questions about your ERP data."],
};

// Screen key(s) guarding a location; an array means any one of them.
export function screenForLocation(pathname, search = "") {
  if (pathname === "/ceo-command-center") return ceoTabScreen(new URLSearchParams(search).get("tab"));
  if (pathname.startsWith("/ceo-command-center/")) {
    const slug = pathname.split("/")[2];
    return (CEO_DETAIL_TABS[slug] || ["overview"]).map((t) => `ceo.${t}`);
  }
  const hit = PATH_SCREENS.find(([p]) => pathname === p || pathname.startsWith(`${p}/`));
  return hit ? hit[1] : "home";
}

export function canSee(user, key) {
  if (!user) return false;
  if (!Array.isArray(user.screens)) return true;
  return [].concat(key).some((k) => user.screens.includes(k));
}

// Landing path for a user, in menu order; null when nothing is assigned.
const LANDING = [
  ["home", "/"],
  ["ceo.overview", "/ceo-command-center"],
  ["ceo.quick-access", "/ceo-command-center?tab=quick-access"],
  ...["buyer", "purchase", "qc", "production", "store", "po", "shipment", "commercial", "comparisons", "master"]
    .map((t) => [`ceo.${t}`, `/ceo-command-center?tab=${t}`]),
  ["dashboards", "/dashboards?view=saved"],
  ...PATH_SCREENS.filter(([, k]) => k !== "dashboards").map(([p, k]) => [k, k === "builder" ? "/builder/new" : p]),
];

export function firstAllowedPath(user) {
  const hit = LANDING.find(([k]) => canSee(user, k));
  return hit ? hit[1] : null;
}
